import { useEffect, useState, type ChangeEvent } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";
import {
  Appearance,
  loadStripe,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import axios from "axios";
import { slackHook } from "src/utils/snug/slackHooks";
import { useRouter } from "src/hooks/use-router";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import { writeStarterPayContext } from "src/sections/starter-pay/context";
import "./starter-pay.css";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
);

type PlanKey = "annual";

interface PlanConfig {
  label: string;
  amount: number;
  display: string;
  todayLabel: string;
  thereafter: string;
  bestFor: string;
  commitment: string;
  onboarding: string;
  onboardingQualifier: string;
  platform: string;
  platformQualifier: string;
  trust: string;
  will: string;
  promo: string;
  promoTerms: string;
  promoBadge: string;
}

const PLANS: Record<PlanKey, PlanConfig> = {
  annual: {
    label: "Starter",
    amount: 3000,
    display: "$3,000",
    todayLabel: "Annual fee",
    thereafter: "",
    bestFor:
      "Teams ready to commit and looking to maximize value from day one.",
    commitment: "Annual",
    onboarding: "Waived",
    onboardingQualifier: "",
    platform: "$3,000/yr",
    platformQualifier: "due today",
    trust: "$300",
    will: "$75",
    promo: "",
    promoTerms: "",
    promoBadge: "",
  },
};

// Fallback values used when the custom plan builder doesn't supply a given
// field. Kept separate from PLANS.annual so the public default plan can
// evolve independently of custom-plan fallbacks.
const CUSTOM_PLAN_BASE: PlanConfig = {
  label: "Custom Plan",
  amount: 3000,
  display: "$3,000",
  todayLabel: "Onboarding fee",
  thereafter: "",
  bestFor: "Teams that want to get started before committing to a full year.",
  commitment: "Monthly",
  onboarding: "$3,000",
  onboardingQualifier: "",
  platform: "$500/mo",
  platformQualifier: "starts month 4",
  trust: "$500",
  will: "$150",
  promo: "",
  promoTerms: "",
  promoBadge: "",
};

// Custom plan via URL params. When ANY of these are present, the page enters
// custom mode: the Monthly/Annual toggle is hidden and the table renders a
// single column populated from the params. Missing params fall back to the
// Annual configuration. `billed` controls the actual amount charged.
const CUSTOM_PARAMS = [
  "label",
  "billed",
  "today_label",
  "thereafter",
  "show_platform_qualifier",
  "onboarding",
  "platform",
  "trust",
  "will",
  "commitment",
  "promo",
  "promo_terms",
  "promo_badge",
] as const;

function formatFee(raw: string | null, fallback: string): string {
  if (raw == null) return fallback;
  const m = raw.match(/^\s*\$?\s*([\d,]+)(.*)$/);
  if (!m) return raw;
  const num = parseInt(m[1].replace(/,/g, ""), 10);
  if (!Number.isFinite(num)) return raw;
  return "$" + num.toLocaleString("en-US") + (m[2] || "");
}

function parseCustomPlan(): PlanConfig | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const hasAny = CUSTOM_PARAMS.some((p) => params.has(p));
  if (!hasAny) return null;

  const base = CUSTOM_PLAN_BASE;
  const billedDigits = (params.get("billed") || "").replace(/[^0-9]/g, "");
  const billed = billedDigits ? parseInt(billedDigits, 10) : 0;
  const hasBilled = billed > 0;

  return {
    label: params.get("label") || "Custom Plan",
    amount: hasBilled ? billed : base.amount,
    display: hasBilled ? "$" + billed.toLocaleString("en-US") : base.display,
    todayLabel: params.get("today_label") || base.todayLabel,
    thereafter: params.get("thereafter") || "",
    bestFor: base.bestFor,
    commitment: params.get("commitment") || base.commitment,
    onboarding: formatFee(params.get("onboarding"), base.onboarding),
    onboardingQualifier: base.onboardingQualifier,
    platform: formatFee(params.get("platform"), base.platform),
    platformQualifier: params.get("show_platform_qualifier") === "1" ? base.platformQualifier : "",
    trust: formatFee(params.get("trust"), base.trust),
    will: formatFee(params.get("will"), base.will),
    promo: params.get("promo") || "",
    promoTerms: params.get("promo_terms") || "",
    promoBadge: params.get("promo_badge") || "",
  };
}

const CUSTOM_PLAN: PlanConfig | null = parseCustomPlan();

const stripeAppearance: Appearance = {
  theme: "stripe",
  variables: {
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fontLineHeight: "1.5",
    borderRadius: "12px",
    colorBackground: "#F2F2EF",
    colorPrimary: "#3045C9",
    colorPrimaryText: "#0D0D12",
    colorText: "#0D0D12",
    colorTextSecondary: "#5A5A68",
    colorTextPlaceholder: "#8E8E9A",
    colorDanger: "#C93050",
    spacingUnit: "4px",
  },
  rules: {
    ".Block": {
      backgroundColor: "var(--colorBackground)",
      boxShadow: "none",
      padding: "12px",
    },
    ".Input": {
      padding: "12px 14px",
      backgroundColor: "#F2F2EF",
      border: "1px solid transparent",
      boxShadow: "none",
    },
    ".Input:focus": {
      backgroundColor: "#FFFFFF",
      border: "1px solid #3045C9",
      boxShadow: "0 0 0 3px rgba(48, 69, 201, 0.12)",
    },
    ".Tab": {
      padding: "10px 12px 8px 12px",
      border: "1px solid rgba(0,0,0,0.06)",
    },
    ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
      border: "1px solid #3045C9",
      backgroundColor: "#FFFFFF",
      boxShadow: "0 0 0 3px rgba(48, 69, 201, 0.12)",
    },
    ".Label": {
      fontWeight: "600",
      fontSize: "11px",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "#8E8E9A",
    },
  },
};

type PaymentSchedule = "single" | "split";

interface FormData {
  name: string;
  company: string;
  email: string;
  plan: PlanKey;
  terms: boolean;
  paymentSchedule: PaymentSchedule;
}

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3 8.5l3 3 7-7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M11.5 2.5l2 2-8 8H3.5v-2l8-8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 4l2 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="7"
        width="10"
        height="7"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5.5 7V5a2.5 2.5 0 015 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckoutSection({
  formData,
  plan,
  todayDisplay,
  isSplit,
  validateForm,
  onSuccess,
}: {
  formData: FormData;
  plan: PlanConfig;
  todayDisplay: string;
  isSplit: boolean;
  validateForm: () => string | null;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!stripe || !elements) return;

    setLoading(true);
    setError("");

    const { paymentIntent, error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: {
          billing_details: {
            name: formData.name,
            email: formData.email,
          },
        },
      },
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message || "Payment failed. Please try again.");
      setLoading(false);
      return;
    }

    if (
      paymentIntent?.status === "succeeded" ||
      paymentIntent?.status === "processing"
    ) {
      slackHook(
        `🎉 STARTER PAYMENT ${paymentIntent.status.toUpperCase()}\n` +
          `Name: ${formData.name}\n` +
          `Company: ${formData.company}\n` +
          `Email: ${formData.email}\n` +
          `Plan: ${plan.label} (${plan.display})\n` +
          `Schedule: ${isSplit ? `Split — ${todayDisplay} today, second ${todayDisplay} in 30 days` : `Single — ${todayDisplay} today`}`,
      );
      onSuccess();
    } else {
      setError("Payment could not be completed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <div className="sp-card">
        <div className="sp-card-head">
          <h2 className="sp-section-title">Payment details</h2>
          <p className="sp-section-help">
            Securely processed by Stripe.{" "}
            {isSplit
              ? `Your card will be charged ${todayDisplay} today and again in 30 days.`
              : "Your card is charged once for the amount shown below."}
          </p>
        </div>
        <div className="sp-stripe">
          <PaymentElement />
        </div>
      </div>

      {error && <div className="sp-error">{error}</div>}

      <button
        type="button"
        className="sp-cta"
        disabled={loading || !stripe}
        onClick={handleSubmit}
      >
        {loading ? (
          <span className="sp-cta-spinner" aria-hidden="true" />
        ) : (
          <LockIcon />
        )}
        Pay {todayDisplay} and get started
      </button>
    </>
  );
}

// The new signup flow lives at /get-started. Top-level visits to /starter-pay
// (from shared links sent before the cutover) redirect there with the full
// query string intact so custom plan params survive. We skip the redirect
// when the page is loaded inside an iframe so the live preview in
// /get-started-link-builder (custom mode) keeps rendering the legacy view.
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin frame access throws — treat as iframe
  }
})();

export default function StarterPayPage() {
  const router = useRouter();
  const [clientSecret, setClientSecret] = useState("");
  const [intentLoading, setIntentLoading] = useState(false);
  const [intentError, setIntentError] = useState("");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    email: "",
    plan: "annual",
    terms: false,
    paymentSchedule: "single",
  });

  const apiBase = process.env.REACT_APP_SNUG_API_URL || "";

  useEffect(() => {
    if (isInIframe) return;
    const target = "/get-started" + window.location.search + window.location.hash;
    window.location.replace(target);
  }, []);

  useEffect(() => {
    if (!CUSTOM_PLAN) return;
    const params = new URLSearchParams(window.location.search);
    let mutated = false;
    for (const p of CUSTOM_PARAMS) {
      if (params.has(p)) {
        params.delete(p);
        mutated = true;
      }
    }
    if (!mutated) return;
    const search = params.toString();
    const newUrl =
      window.location.pathname +
      (search ? `?${search}` : "") +
      window.location.hash;
    window.history.replaceState(window.history.state, "", newUrl);
  }, []);

  const isCustom = CUSTOM_PLAN !== null;
  const plan: PlanConfig = isCustom
    ? (CUSTOM_PLAN as PlanConfig)
    : PLANS[formData.plan];

  const isSplitEligible = !isCustom;
  const isSplit = isSplitEligible && formData.paymentSchedule === "split";
  const todayAmount = isSplit ? plan.amount / 2 : plan.amount;
  const todayDisplay = "$" + todayAmount.toLocaleString("en-US");

  if (!isGetSnugDomain) return null;

  const tiers: {
    key: string;
    label: string;
    config: PlanConfig;
    isActive: boolean;
  }[] = isCustom
    ? [{ key: "custom", label: plan.label, config: plan, isActive: true }]
    : [{ key: "annual", label: "Starter", config: PLANS.annual, isActive: true }];

  const activeTier = tiers.find((t) => t.isActive) ?? tiers[0];

  const planRows: { label: string; qualifier?: string; values: string[] }[] = [
    ...(isCustom
      ? [
          {
            label: "Onboarding fee",
            qualifier: activeTier.config.onboardingQualifier || undefined,
            values: tiers.map((t) => t.config.onboarding),
          },
        ]
      : []),
    {
      label: "Platform fee",
      qualifier: isSplit ? undefined : activeTier.config.platformQualifier || undefined,
      values: tiers.map((t) => t.config.platform),
    },
    { label: "Trust fee", values: tiers.map((t) => t.config.trust) },
    { label: "Will fee", values: tiers.map((t) => t.config.will) },
    ...(isCustom
      ? [{ label: "Commitment", values: tiers.map((t) => t.config.commitment) }]
      : []),
    {
      label: "Billed today",
      qualifier: isSplit ? `then ${todayDisplay} in 30 days` : undefined,
      values: tiers.map((t) => (isSplitEligible ? todayDisplay : t.config.display)),
    },
  ];

  const setField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData((f) => ({ ...f, [key]: value }));
    if (key === "name" || key === "company" || key === "email") {
      setIntentError("");
    }
  };

  const validateForm = (): string | null => {
    if (
      !formData.name.trim() ||
      !formData.company.trim() ||
      !formData.email.trim()
    ) {
      return "Please fill in all fields.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return "Please enter a valid email address.";
    }
    if (!formData.terms) {
      return "You must agree to the Terms and Conditions.";
    }
    return null;
  };

  const isLocked = clientSecret !== "";

  const handleEdit = () => {
    setClientSecret("");
    setIntentError("");
  };

  const handleContinue = async () => {
    const validationError = validateForm();
    if (validationError) {
      setIntentError(validationError);
      return;
    }
    setIntentLoading(true);
    setIntentError("");
    try {
      const payload: Record<string, unknown> = {
        plan: isCustom ? "custom" : formData.plan,
        name: formData.name,
        company: formData.company,
        email: formData.email,
        payment_schedule: isSplit ? "split" : "single",
      };
      if (isCustom) {
        payload.amount = plan.amount;
        payload.label = plan.label;
      }
      const { data } = await axios.post(
        `${apiBase}/v3/starter-pay/payment-intent/`,
        payload,
      );
      setClientSecret(data.client_secret);
    } catch (e: any) {
      setIntentError(
        e?.response?.data?.error ||
          "Could not initialize payment. Please try again.",
      );
    } finally {
      setIntentLoading(false);
    }
  };


  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started with <em>Snug.</em>
        </h1>

        <p className="sp-sub">
          {isCustom
            ? "Your custom plan is set up below. Review the details, enter your information, and complete your secure payment to kick off onboarding."
            : "Includes full platform access, white-label setup, and onboarding support."}
        </p>

        <SignupStepper currentStep="payment" />

        <div className="sp-card sp-plan-card">
          <div className="sp-card-head">
            <span className="sp-card-eyebrow">Plan details</span>
          </div>
          <table className="sp-plan-table">
            <tbody>
              {planRows.map((row) => (
                <tr key={row.label}>
                  <th scope="row" className="sp-plan-row-label">
                    {row.label}
                    {row.qualifier && (
                      <span className="sp-plan-row-qualifier">
                        ({row.qualifier})
                      </span>
                    )}
                  </th>
                  {row.values.map((v, i) => (
                    <td
                      key={i}
                      className={`sp-plan-cell${tiers[i].isActive ? " is-active" : ""}`}
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {isSplitEligible && (
            <div className="sp-pay-schedule">
              <div
                className="sp-pay-schedule-options"
                role="radiogroup"
                aria-label="Payment schedule"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={formData.paymentSchedule === "single"}
                  className={`sp-pay-schedule-option${formData.paymentSchedule === "single" ? " is-active" : ""}`}
                  onClick={() => setField("paymentSchedule", "single")}
                  disabled={isLocked}
                >
                  1 payment
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={formData.paymentSchedule === "split"}
                  className={`sp-pay-schedule-option${formData.paymentSchedule === "split" ? " is-active" : ""}`}
                  onClick={() => setField("paymentSchedule", "split")}
                  disabled={isLocked}
                >
                  2 payments
                </button>
              </div>
            </div>
          )}

          {plan.thereafter && (
            <p
              style={{
                margin: "1rem 0 0.5rem",
                padding: "0 8px",
                fontWeight: 600,
                fontSize: "13.5px",
                color: "#5A5A68",
              }}
            >
              Note: {plan.thereafter}
            </p>
          )}
        </div>

        {plan.promo && (
          <div className="sp-promo-banner" role="note">
            <div className="sp-promo-icon" aria-hidden="true">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16.4l-6.4 4.8 2.4-7.2-6-4.8h7.6L12 2z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="sp-promo-content">
              <span className="sp-promo-title">{plan.promo}</span>
              {plan.promoTerms && (
                <span className="sp-promo-terms">{plan.promoTerms}</span>
              )}
            </div>
            {plan.promoBadge && (
              <span className="sp-promo-badge">{plan.promoBadge}</span>
            )}
          </div>
        )}

        <div className="sp-card">
          <div className="sp-card-head sp-card-head-row">
            <div>
              <h2 className="sp-section-title">Your details</h2>
              <p className="sp-section-help">
                Tell us who you are so we can set up your account and send
                onboarding materials.
              </p>
            </div>
            {isLocked && (
              <button
                type="button"
                className="sp-edit-btn"
                onClick={handleEdit}
              >
                <PencilIcon />
                Edit
              </button>
            )}
          </div>
          <div className="sp-input-grid sp-input-grid-1">
            <label className={`sp-input${isLocked ? " is-locked" : ""}`}>
              <span className="sp-input-label">Full name</span>
              <input
                type="text"
                className="sp-input-field"
                value={formData.name}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setField("name", e.target.value)
                }
                placeholder="Jane Doe"
                disabled={isLocked}
              />
            </label>
            <label className={`sp-input${isLocked ? " is-locked" : ""}`}>
              <span className="sp-input-label">Company name</span>
              <input
                type="text"
                className="sp-input-field"
                value={formData.company}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setField("company", e.target.value)
                }
                placeholder="Acme Advisors"
                disabled={isLocked}
              />
            </label>
            <label className={`sp-input${isLocked ? " is-locked" : ""}`}>
              <span className="sp-input-label">Work email</span>
              <input
                type="email"
                className="sp-input-field"
                value={formData.email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setField("email", e.target.value)
                }
                placeholder="jane@acme.com"
                disabled={isLocked}
              />
            </label>
          </div>

          <label className="sp-check" style={{ marginTop: 16 }}>
            <input
              type="checkbox"
              checked={formData.terms}
              onChange={(e) => setField("terms", e.target.checked)}
              disabled={isLocked}
            />
            <span className="sp-check-box" aria-hidden="true">
              <CheckIcon />
            </span>
            <span className="sp-check-label">
              I have read and agree to the{" "}
              <a
                href="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/Professional+Terms+of+Service.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </a>
              .
            </span>
          </label>
        </div>

        {clientSecret ? (
          <Elements
            stripe={stripePromise}
            options={
              {
                clientSecret,
                appearance: stripeAppearance,
                loader: "auto",
              } as StripeElementsOptions
            }
          >
            <CheckoutSection
              formData={formData}
              plan={plan}
              todayDisplay={todayDisplay}
              isSplit={isSplit}
              validateForm={validateForm}
              onSuccess={() => {
                writeStarterPayContext({
                  name: formData.name,
                  company: formData.company,
                  email: formData.email,
                  plan: isCustom ? "custom" : formData.plan,
                  planLabel: plan.label,
                  paymentSchedule: isSplit ? "split" : "single",
                });
                router.push("/get-started-onboarding");
              }}
            />
          </Elements>
        ) : (
          <>
            {intentError && <div className="sp-error">{intentError}</div>}
            <button
              type="button"
              className="sp-cta"
              disabled={intentLoading}
              onClick={handleContinue}
            >
              {intentLoading ? (
                <span className="sp-cta-spinner" aria-hidden="true" />
              ) : (
                <LockIcon />
              )}
              Continue to payment
            </button>
          </>
        )}

        <p className="sp-disclaimer">
          Your card is processed securely by Stripe. We never store card
          details on our servers.
        </p>
      </section>
    </div>
  );
}
